import { Search } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  cn,
} from '@design-system';
import {
  PERIODS,
  useConversationLog,
  type ConversationSummary,
  type Period,
} from '../hooks/use-magic-email-conversations';
import { StatusPill } from './StatusPill';
import { ThreadDetail } from './ThreadDetail';

const when = (iso: string) => {
  const date = new Date(iso);
  if (date.toDateString() === new Date().toDateString())
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date.toDateString() === yesterday.toDateString()
    ? 'Yesterday'
    : date.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

function Outcome({ conversation }: { conversation: ConversationSummary }) {
  const { latest } = conversation;
  if (latest.outcome === 'handed_off') return <StatusPill tone="changes">With our team</StatusPill>;
  if (latest.outcome === 'sent') return <StatusPill tone="live">Answered</StatusPill>;
  return <StatusPill tone="draft">Answered by the old assistant</StatusPill>;
}

/** Every conversation the assistant has had: a list on the left, the open one on the right. */
export function Conversations() {
  const log = useConversationLog();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex h-11 flex-[1_1_360px] items-center gap-2.5 rounded-[10px] border border-zinc-300 bg-white px-3.5">
          <Search
            className="size-[18px] text-zinc-500"
            aria-hidden
          />
          <span className="sr-only">Find a conversation</span>
          <Input
            value={log.search}
            onChange={event => log.setSearch(event.target.value)}
            placeholder="Find by advisor email"
            className="h-auto border-0 p-0 text-[15px] shadow-none focus-visible:ring-0"
          />
        </label>
        <Select
          value={log.period}
          onValueChange={value => log.setPeriod(value as Period)}
        >
          <SelectTrigger
            className="h-11 w-44 bg-white"
            aria-label="When"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map(period => (
              <SelectItem
                key={period.value}
                value={period.value}
              >
                {period.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          aria-pressed={log.live}
          onClick={() => log.setLive(!log.live)}
          className={cn(
            'inline-flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-semibold',
            log.live
              ? 'border-[#1E8E4A] bg-[#E7F6EC] text-[#1B6B3A]'
              : 'border-zinc-300 bg-white text-zinc-700',
          )}
        >
          <span
            className={cn('size-2 rounded-full', log.live ? 'bg-[#1E8E4A]' : 'bg-zinc-400')}
            aria-hidden
          />
          {log.live ? 'Live: new emails appear on their own' : 'Live updates paused'}
        </button>
      </div>

      {log.query.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : log.query.isError ? (
        <p className="text-sm text-red-700">
          Could not load conversations: {log.query.error.message}
        </p>
      ) : log.conversations.length === 0 ? (
        <p className="text-sm text-zinc-600">No conversations match.</p>
      ) : (
        <div className="flex flex-wrap items-start gap-6">
          <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-2">
            {log.conversations.map(conversation => (
              <button
                key={`${conversation.advisorId}|${conversation.threadKey}`}
                type="button"
                onClick={() => log.open(conversation)}
                className={cn(
                  'flex flex-col gap-1.5 rounded-xl bg-white px-4 py-3.5 text-left',
                  log.isOpen(conversation)
                    ? 'border-2 border-[#5B3FD6]'
                    : 'border border-zinc-200 hover:border-zinc-300',
                )}
              >
                <span className="flex justify-between gap-2">
                  <span className="truncate text-sm font-semibold">
                    {conversation.advisorEmail}
                  </span>
                  <span className="shrink-0 text-[13px] text-zinc-500">
                    {when(conversation.lastDateTime)}
                  </span>
                </span>
                <span className="line-clamp-2 text-sm text-zinc-700">{conversation.excerpt}</span>
                <span className="self-start">
                  <Outcome conversation={conversation} />
                </span>
              </button>
            ))}
            {log.query.hasNextPage && (
              <Button
                variant="outline"
                className="h-11"
                onClick={() => log.query.fetchNextPage()}
                disabled={log.query.isFetchingNextPage}
              >
                {log.query.isFetchingNextPage ? 'Loading…' : 'Older conversations'}
              </Button>
            )}
          </div>
          <div className="min-w-0 flex-[999_1_640px]">
            {log.selected && (
              <ThreadDetail
                advisorId={log.selected.advisorId}
                threadKey={log.selected.threadKey}
                live={log.live}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
